import {
  Battle_Action,
  Direction,
  Position,
  BattlePokemonData,
  StatusEffect,
  StatusEffectInstance,
  SkillStatusEffect,
} from "types/battle-engine/battle";
import { PokemonSkill } from "./pokemon-skill";
import { getDodgeChance } from "game/duel-engine/utils/stat-scaling";

export class Pokemon {
  public id: number;
  public name: string;
  public base_pokemon_name: string;
  public direction: Direction;

  protected battleActions: Battle_Action[];
  protected enemies: Pokemon[];
  protected allies: Pokemon[];
  protected targetEnemy: Pokemon | null = null;
  protected position: Position = { x: 0, y: 0 };

  protected currentSkillRange = {
    min: 0,
    max: 0,
  };

  protected hp: {
    current: number;
    max: number;
  };
  protected pendingDamage: number = 0; // Tracks damage scheduled but not yet applied
  protected shield: {
    amount: number;
    durationTicks: number;
  };
  protected ability_power: number;
  protected resistance: number;
  protected speed: number;
  protected pokemonData: BattlePokemonData;
  protected skills: PokemonSkill[];

  // Status effects tracking
  protected activeStatusEffects: Map<StatusEffect, StatusEffectInstance> =
    new Map();

  constructor(data: BattlePokemonData) {
    this.pokemonData = data;
    this.id = data.id;
    this.name = data.name;
    this.base_pokemon_name = data.base_pokemon_name;
    this.skills = data.skills.map((skill) => new PokemonSkill(skill, this));
    this.hp = {
      current: data.hp,
      max: data.max_hp,
    };
    this.ability_power = data.ability_power;
    this.resistance = data.resistance;
    this.speed = data.speed ?? 100;
    this.position = { x: 0, y: 0 };
    this.direction = Direction.S;
    this.battleActions = [];
    this.enemies = [];
    this.allies = [];
    this.shield = {
      amount: 0,
      durationTicks: 0,
    };
  }

  public init(enemies: Pokemon[], allies: Pokemon[]) {
    this.enemies = enemies;
    this.allies = allies;
  }

  /**
   * Applies damage to the Pokemon, factoring in shield absorption
   * @param amount - Damage amount
   * @param skipAnimation - If true, skip hurt animation (for DoT/subtle damage)
   * @returns true if the damage was fatal (HP reached 0), false otherwise
   */
  public async takeDamage(
    amount: number,
    skipAnimation: boolean = false,
  ): Promise<boolean> {
    const wasAlive = this.hp.current > 0;

    // Check if shield can absorb the damage
    if (this.shield.amount > 0) {
      if (amount <= this.shield.amount) {
        // Shield absorbs all damage
        this.shield.amount -= amount;
        return false; // Not fatal if shield absorbed everything
      } else {
        // Shield is depleted, overflow damage goes to HP
        const overflow = amount - this.shield.amount;
        this.shield.amount = 0;
        this.shield.durationTicks = 0;
        this.hp.current = Math.max(0, this.hp.current - overflow);
        return wasAlive && this.hp.current <= 0; // Fatal if HP reached 0
      }
    }

    // No shield, apply damage directly to HP
    this.hp.current = Math.max(0, this.hp.current - amount);
    return wasAlive && this.hp.current <= 0; // Fatal if HP reached 0
  }

  /**
   * Calculate what HP will be after taking damage, accounting for pending damage and shield.
   * Used by server to pre-calculate result_hp for battle log.
   */
  public calculateResultHP(incomingDamage: number): number {
    const totalDamage = this.pendingDamage + incomingDamage;

    // Shield absorbs damage first
    if (this.shield.amount >= totalDamage) {
      return this.hp.current; // Shield absorbs all damage
    } else if (this.shield.amount > 0) {
      const overflow = totalDamage - this.shield.amount;
      return Math.max(0, this.hp.current - overflow);
    }

    // No shield, damage goes directly to HP
    return Math.max(0, this.hp.current - totalDamage);
  }

  /**
   * Register damage that will be applied later (for HP calculation purposes)
   */
  public registerPendingDamage(amount: number) {
    this.pendingDamage += amount;
  }

  /**
   * Clear pending damage when it's actually applied
   */
  public clearPendingDamage(amount: number) {
    this.pendingDamage = Math.max(0, this.pendingDamage - amount);
  }

  /**
   * Heals the Pokemon
   * @param amount - Heal amount
   * @returns The actual amount healed (capped by max HP)
   */
  public heal(amount: number): number {
    const currentHp = this.hp.current;
    const newHp = Math.min(this.hp.max, currentHp + amount);
    const actualHealAmount = newHp - currentHp;

    this.hp.current = newHp;
    return actualHealAmount;
  }

  public addShield(amount: number, durationTicks: number) {
    const shieldDurationTicks = Math.max(
      durationTicks,
      this.shield.durationTicks,
    );
    const shieldAmount = this.shield.amount + amount;

    this.shield = {
      amount: shieldAmount,
      durationTicks: shieldDurationTicks,
    };
  }

  /**
   * Decrements shield duration each tick.
   * When duration expires, removes shield instantly.
   */
  public tickShield() {
    if (this.shield.durationTicks > 0) {
      this.shield.durationTicks--;
      if (this.shield.durationTicks <= 0) {
        // Instant removal when expired
        this.shield.amount = 0;
      }
    }
  }

  public getEnemyPokemon = (id: number): Pokemon | null => {
    return this.enemies.find((enemy) => enemy.id === id) ?? null;
  };

  public getAllyPokemon = (id: number): Pokemon | null => {
    return this.allies.find((ally) => ally.id === id) ?? null;
  };

  // -- Getters --
  public get HP(): { current: number; max: number } {
    return this.hp;
  }

  public get Skills(): PokemonSkill[] {
    return this.skills;
  }

  public get BattleActions(): Battle_Action[] {
    return this.battleActions;
  }

  public get Position(): Position {
    return this.position;
  }

  public get Speed(): number {
    return this.speed;
  }

  public get Enemies(): Pokemon[] {
    return this.enemies;
  }

  public get Allies(): Pokemon[] {
    return this.allies;
  }

  public get PendingDamage(): number {
    return this.pendingDamage;
  }

  public get Shield(): { amount: number; durationTicks: number } {
    return this.shield;
  }

  public set Position(position: Position) {
    this.position = position;
  }

  // -- Status Effect Methods --

  /**
   * Map of opposite status effect pairs for cancellation logic
   */
  private static readonly OPPOSITE_EFFECTS: Map<StatusEffect, StatusEffect> =
    new Map([
      [StatusEffect.SPEED_UP, StatusEffect.SPEED_DOWN],
      [StatusEffect.SPEED_DOWN, StatusEffect.SPEED_UP],
      [StatusEffect.ATTACK_UP, StatusEffect.ATTACK_DOWN],
      [StatusEffect.ATTACK_DOWN, StatusEffect.ATTACK_UP],
      [StatusEffect.DEFENSE_UP, StatusEffect.DEFENSE_DOWN],
      [StatusEffect.DEFENSE_DOWN, StatusEffect.DEFENSE_UP],
    ]);

  /**
   * Applies a status effect to the Pokemon, handling stacking and opposite effect cancellation
   */
  public applyStatusEffect(
    effect: SkillStatusEffect,
    sourceId: number,
    currentTick: number,
  ): void {
    const oppositeEffect = Pokemon.OPPOSITE_EFFECTS.get(effect.status);

    // Check if opposite effect exists
    if (oppositeEffect && this.activeStatusEffects.has(oppositeEffect)) {
      const existing = this.activeStatusEffects.get(oppositeEffect)!;
      const netIntensity = existing.intensity - effect.intensity;

      if (netIntensity === 0) {
        // Complete cancellation - remove the opposite effect
        this.activeStatusEffects.delete(oppositeEffect);
        return;
      } else if (netIntensity > 0) {
        // Existing effect dominates - reduce its intensity
        existing.intensity = netIntensity;
        return;
      } else {
        // New effect dominates - remove opposite and apply new with remaining intensity
        this.activeStatusEffects.delete(oppositeEffect);
        // Continue to apply the new effect with adjusted intensity
        effect = {
          ...effect,
          intensity: Math.abs(netIntensity),
        };
      }
    }

    // Get existing effect of the same type (if any)
    const existingEffect = this.activeStatusEffects.get(effect.status);

    // Determine stacking behavior based on effect category
    if (this.isCrowdControl(effect.status)) {
      // Crowd control: Add duration
      if (existingEffect) {
        existingEffect.durationTicks += effect.duration_ticks;
      } else {
        this.activeStatusEffects.set(effect.status, {
          status: effect.status,
          intensity: effect.intensity,
          durationTicks: effect.duration_ticks,
          sourceId,
          appliedAtTick: currentTick,
        });
      }
    } else {
      // DoT and Stat Modifiers: Stack intensity, max duration
      if (existingEffect) {
        console.log(`re-applying ${effect.status} to ${this.name}`);
        existingEffect.intensity += effect.intensity;
        existingEffect.durationTicks = Math.max(
          existingEffect.durationTicks,
          effect.duration_ticks,
        );
      } else {
        console.log(`applying ${effect.status} to ${this.name}`);
        this.activeStatusEffects.set(effect.status, {
          status: effect.status,
          intensity: effect.intensity,
          durationTicks: effect.duration_ticks,
          sourceId,
          appliedAtTick: currentTick,
        });
      }
    }
  }

  /**
   * Process all active status effects for this tick
   */
  public tickStatusEffects(currentTick: number): void {
    const effectsToRemove: StatusEffect[] = [];
    const TICKS_PER_SECOND = 60; // DoT should apply once per second

    for (const [status, effect] of this.activeStatusEffects) {
      // Process effect based on type
      if (this.isDoT(status)) {
        // Apply DoT damage once per second, not every tick
        const lastDamageTick = effect.lastDamageAtTick ?? effect.appliedAtTick;
        const ticksSinceLastDamage = currentTick - lastDamageTick;

        if (ticksSinceLastDamage >= TICKS_PER_SECOND) {
          this.applyDoTDamage(effect);
          effect.lastDamageAtTick = currentTick;
        }
      }
      // Stat modifiers are applied via getters, no per-tick processing needed
      // Crowd control is checked in shouldAct(), no per-tick processing needed

      // Decrement duration
      effect.durationTicks--;

      // Mark for removal if expired or intensity reaches zero
      if (effect.durationTicks <= 0 || effect.intensity <= 0) {
        effectsToRemove.push(status);
      }
    }

    // Remove expired effects
    for (const status of effectsToRemove) {
      this.removeStatusEffect(status);
    }
  }

  /**
   * Remove a status effect from the Pokemon
   * Can be overridden by subclasses for visual effects (e.g., knockup animation end)
   */
  public removeStatusEffect(status: StatusEffect): boolean {
    return this.activeStatusEffects.delete(status);
  }

  /**
   * Apply damage from DoT effects
   * Can be overridden by subclasses (e.g., ServerPokemon to track stats)
   */
  protected applyDoTDamage(effect: StatusEffectInstance): void {
    // Fixed damage per intensity per tick
    const damagePerIntensity = 2; // BURN = 2, POISON = 2
    const totalDamage = effect.intensity * damagePerIntensity;

    this.takeDamage(totalDamage, true); // Skip hurt animation for DoT
  }

  /**
   * Check if Pokemon has a crowd control effect active
   */
  public hasCrowdControl(): boolean {
    return (
      this.activeStatusEffects.has(StatusEffect.STUN) ||
      this.activeStatusEffects.has(StatusEffect.SLEEP) ||
      this.activeStatusEffects.has(StatusEffect.KNOCKUP)
    );
  }

  /**
   * Check if effect is a crowd control type
   */
  private isCrowdControl(status: StatusEffect): boolean {
    return (
      status === StatusEffect.STUN ||
      status === StatusEffect.SLEEP ||
      status === StatusEffect.KNOCKUP
    );
  }

  /**
   * Check if effect is a DoT (Damage over Time) type
   */
  private isDoT(status: StatusEffect): boolean {
    return status === StatusEffect.BURN || status === StatusEffect.POISON;
  }

  /**
   * Get effective speed with status effect modifiers applied
   */
  public get effectiveSpeed(): number {
    let speedMultiplier = 1;

    const speedUp = this.activeStatusEffects.get(StatusEffect.SPEED_UP);
    const speedDown = this.activeStatusEffects.get(StatusEffect.SPEED_DOWN);

    if (speedUp) {
      speedMultiplier += speedUp.intensity * 0.15; // +15% per intensity
    }
    if (speedDown) {
      speedMultiplier -= speedDown.intensity * 0.15; // -15% per intensity
    }

    return Math.max(10, this.speed * speedMultiplier); // Minimum 10% of base speed
  }

  /**
   * Get effective ability power with status effect modifiers applied
   */
  public get effectiveAbilityPower(): number {
    let powerMultiplier = 1;

    const attackUp = this.activeStatusEffects.get(StatusEffect.ATTACK_UP);
    const attackDown = this.activeStatusEffects.get(StatusEffect.ATTACK_DOWN);

    if (attackUp) {
      powerMultiplier += attackUp.intensity * 0.2; // +20% per intensity
    }
    if (attackDown) {
      powerMultiplier -= attackDown.intensity * 0.2; // -20% per intensity
    }

    return Math.max(1, this.ability_power * powerMultiplier);
  }

  /**
   * Get effective resistance with status effect modifiers applied
   */
  public get effectiveResistance(): number {
    let resistanceMultiplier = 1;

    const defenseUp = this.activeStatusEffects.get(StatusEffect.DEFENSE_UP);
    const defenseDown = this.activeStatusEffects.get(StatusEffect.DEFENSE_DOWN);

    if (defenseUp) {
      resistanceMultiplier += defenseUp.intensity * 0.2; // +20% per intensity
    }
    if (defenseDown) {
      resistanceMultiplier -= defenseDown.intensity * 0.2; // -20% per intensity
    }

    return Math.max(0, this.resistance * resistanceMultiplier);
  }

  /**
   * Get effective dodge chance based on speed
   */
  public get dodgeChance(): number {
    return getDodgeChance(this.effectiveSpeed);
  }

  /**
   * Get all active status effects (for UI display)
   */
  public get ActiveStatusEffects(): Map<StatusEffect, StatusEffectInstance> {
    return this.activeStatusEffects;
  }

  public get PokemonData(): BattlePokemonData {
    return this.pokemonData;
  }
}
